import { FormNumberField } from '../fields/FormNumberField'
import { FormStringField } from '../fields/FormStringField'
import { FormFieldData, FormFieldType } from '../fields/type'
import { FormTextField } from '../fields/FormTextField'
import { FormDateField } from '../fields/FormDateField'
import { FormSelectField } from '../fields/FormSelectField'
import { FormCheckboxField } from '../fields/FormCheckboxField'
import { FormCheckboxGroupField } from '../fields/FormCheckboxGroupField'
import { FormFileField } from '../fields/FormFileField'
import { FormCustomReadOnlyField } from '../fields/FormCustomReadOnlyField'
import { FormEmbedField } from '../fields/FormEmbedField'
import { FormColorField } from '../fields/FormColorField'
import { FormKeywordsField } from '../fields/FormKeywordsField'
import { FormMessage } from '@/components/ui/form'
import { FormControl, FormField } from '@/components/ui/form'
import { FormLabel } from '@/components/ui/form'
import { FormItem } from '@/components/ui/form'
import { useCallback } from 'react'
import { useDetailForm } from '../FormProviderV2'
import { ControllerRenderProps } from 'react-hook-form'
import { FieldValues } from 'react-hook-form'
import { FormRadioGroupField } from '../fields/FormRadioGroupField'
import { FormCKEditorField } from '../fields/FormCKEditorField'
import clsx from 'clsx'
import { FormRelationField } from '../fields/FormRelationField'
import { FormReorderField } from '../fields/FormReorderField'
import { FormJsonField } from '../fields/FormJsonField'
import { FormSwitchField } from '../fields/FormSwitchField'
import { FormHashtagField } from '../fields/FormHashtagField'

type RenderType = 'dialog' | 'detail'

type Props<T> = {
  renderType: RenderType
  fieldData: FormFieldData
  hideLabel?: boolean
}

export const FormFieldRenderer = <T,>({
  renderType,
  fieldData,
  hideLabel = false,
}: Props<T>) => {
  const context = useDetailForm()

  // useLayoutEffect(() => {
  //   if (fieldData.default) {
  //     console.log(fieldData.key, fieldData.default)
  //     context.formProps.form?.setValue(fieldData.key, fieldData.default)
  //   }
  // }, [fieldData.default, context.formProps.form, fieldData.key])

  const renderField = useCallback(
    (
      fieldData: FormFieldData,
      field: ControllerRenderProps<FieldValues, string>,
    ) => {
      switch (fieldData.type) {
        case FormFieldType.NUMBER:
          return <FormNumberField data={fieldData} field={field} />
        case FormFieldType.STRING:
          return <FormStringField data={fieldData} field={field} />
        case FormFieldType.TEXT:
          return <FormTextField data={fieldData} field={field} />
        case FormFieldType.DATE:
          return <FormDateField data={fieldData} field={field} />
        case FormFieldType.SELECT:
          return <FormSelectField data={fieldData} field={field} />
        case FormFieldType.CHECKBOX:
          return <FormCheckboxField data={fieldData} field={field} />
        case FormFieldType.CHECKBOXGROUP:
          return <FormCheckboxGroupField data={fieldData} field={field} />
        case FormFieldType.FILE:
          return <FormFileField data={fieldData} field={field} />
        case FormFieldType.CUSTOM_READ_ONLY:
          return <FormCustomReadOnlyField data={fieldData} field={field} />
        case FormFieldType.EMBED:
          return <FormEmbedField data={fieldData} field={field} />
        case FormFieldType.COLOR:
          return <FormColorField data={fieldData} field={field} />
        case FormFieldType.KEYWORDS:
          return <FormKeywordsField data={fieldData} field={field} />
        case FormFieldType.RADIOGROUP:
          return <FormRadioGroupField data={fieldData} field={field} />
        case FormFieldType.CKEDITOR:
          return <FormCKEditorField data={fieldData} field={field} />
        case FormFieldType.RELATION:
          return <FormRelationField data={fieldData} field={field} />
        case FormFieldType.REORDER:
          return <FormReorderField data={fieldData} field={field} />
        case FormFieldType.JSON:
          return <FormJsonField data={fieldData} field={field} />
        case FormFieldType.SWITCH:
          return <FormSwitchField data={fieldData} field={field} />
        case FormFieldType.HASHTAG:
          return <FormHashtagField data={fieldData} field={field} />
        default:
          throw new Error(
            `UnknownFieldTypeError ${JSON.stringify(fieldData, null, 2)}`,
          )
      }
    },
    [],
  )

  const renderContainer = useCallback(
    (
      fieldData: FormFieldData,
      field: ControllerRenderProps<FieldValues, string>,
      direction: 'row' | 'column' = renderType === 'dialog' ? 'column' : 'row',
    ) => {
      if (renderType === 'dialog') {
        return (
          <FormItem key={fieldData.key}>
            {!hideLabel && <FormLabel>{fieldData.label}</FormLabel>}
            <FormControl>{renderField(fieldData, field)}</FormControl>
            <FormMessage />
          </FormItem>
        )
      }

      if (renderType === 'detail') {
        return (
          <FormItem
            key={fieldData.key}
            className={clsx('flex h-full min-h-10 space-y-0 hover:bg-gray-50', {
              'flex-col': direction === 'column',
            })}
          >
            {!hideLabel && (
              <FormLabel
                className={clsx(
                  'flex w-40 shrink-0 items-center justify-center bg-slate-100 px-2 text-[14px] font-medium',
                  {
                    'w-full h-8': direction === 'column',
                    '!font-bold': fieldData.required,
                  },
                )}
              >
                {fieldData.label}
                {fieldData.required && <span>*</span>}
              </FormLabel>
            )}
            <FormControl>
              <div className="flex-1 px-2 py-1.5 text-[13px]">
                {renderField(fieldData, field)}
                <FormMessage className="mt-1 text-[13px]" />
              </div>
            </FormControl>
          </FormItem>
        )
      }

      throw new Error('UnknownRenderType')
    },
    [renderType, renderField, hideLabel],
  )

  if (fieldData.type === FormFieldType.FIELD_GROUP_READ_ONLY) {
    return (
      <table className="h-px w-full">
        <tbody>
          <tr>
            {fieldData.fields.map((child) => {
              return (
                <td className="py-0">
                  <div className="h-full">
                    <FormField
                      control={context.form?.control}
                      key={child.key}
                      name={child.key}
                      render={({ field }) =>
                        renderContainer(child, field, fieldData.fieldDirection)
                      }
                    />
                  </div>
                </td>
              )
            })}
          </tr>
        </tbody>
      </table>
    )
  }

  return (
    <FormField
      control={context.form?.control}
      key={fieldData.key}
      name={fieldData.key}
      render={({ field }) => renderContainer(fieldData, field)}
    />
  )
}
